/**
 * Invoice list Date filter presets (Today / Yesterday / Week / Month / Custom / All).
 * Pure helpers so unit tests can pin a fixed "today" without flaking.
 */

export type DateRangeMode =
  | "today"
  | "yesterday"
  | "week"
  | "month"
  | "custom"
  | "all";

export const DATE_PRESET_OPTIONS: { value: DateRangeMode; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "custom", label: "Custom Range" },
  { value: "all", label: "All Time" },
];

/** Page size for Week / Month / Custom / All Time (Today & Yesterday load all). */
export const INVOICE_LIST_PAGE_SIZE = 20;

/** Modes that load every matching row and show a totals footer (no pager). */
export function isSummaryDateMode(mode: DateRangeMode): boolean {
  return mode === "today" || mode === "yesterday";
}

export function ymdFromLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function parseLocalYmd(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd).trim());
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

export function addDaysYmd(ymd: string, delta: number): string {
  const d = parseLocalYmd(ymd);
  if (!d) return ymd;
  d.setDate(d.getDate() + delta);
  return ymdFromLocalDate(d);
}

export function startOfWeekMonday(ymd: string): string {
  const d = parseLocalYmd(ymd);
  if (!d) return ymd;
  const day = d.getDay(); // 0 Sun … 6 Sat
  const back = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - back);
  return ymdFromLocalDate(d);
}

export function endOfWeekSunday(ymd: string): string {
  return addDaysYmd(startOfWeekMonday(ymd), 6);
}

export function startOfMonthYmd(ymd: string): string {
  const d = parseLocalYmd(ymd);
  if (!d) return ymd;
  d.setDate(1);
  return ymdFromLocalDate(d);
}

export function endOfMonthYmd(ymd: string): string {
  const d = parseLocalYmd(ymd);
  if (!d) return ymd;
  d.setMonth(d.getMonth() + 1, 0);
  return ymdFromLocalDate(d);
}

export function formatDisplayYmd(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd).trim());
  if (!m) return ymd;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export function rangeForDateMode(
  mode: DateRangeMode,
  customStart?: string,
  customEnd?: string,
  todayYmd?: string
): { start: string; end: string } {
  const today =
    todayYmd && /^\d{4}-\d{2}-\d{2}$/.test(todayYmd)
      ? todayYmd
      : ymdFromLocalDate(new Date());
  switch (mode) {
    case "today":
      return { start: today, end: today };
    case "yesterday": {
      const y = addDaysYmd(today, -1);
      return { start: y, end: y };
    }
    case "week":
      return { start: startOfWeekMonday(today), end: endOfWeekSunday(today) };
    case "month":
      return { start: startOfMonthYmd(today), end: endOfMonthYmd(today) };
    case "custom":
      return {
        start:
          customStart && /^\d{4}-\d{2}-\d{2}$/.test(customStart)
            ? customStart
            : today,
        end:
          customEnd && /^\d{4}-\d{2}-\d{2}$/.test(customEnd) ? customEnd : today,
      };
    case "all":
    default:
      return { start: "", end: "" };
  }
}

export function dateTriggerLabel(
  mode: DateRangeMode,
  start: string,
  end: string
): string {
  const preset =
    DATE_PRESET_OPTIONS.find((o) => o.value === mode)?.label ?? "Date";
  if (mode === "all") return "All Time";
  if (!start || !end) return preset;
  if (start === end) {
    if (mode === "today" || mode === "yesterday") {
      return `${preset} · ${formatDisplayYmd(start)}`;
    }
    return formatDisplayYmd(start);
  }
  return `${formatDisplayYmd(start)} – ${formatDisplayYmd(end)}`;
}
